import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown, { Components } from 'react-markdown';

import { Box, Link, Stack, StackProps, Typography } from '@mui/material';

import { LocationUtils, useStrOfferingData } from '@arrived/common';
import { AssetType, Offering } from '@arrived/models';

interface PropertyDetailsDescriptionProps extends StackProps {
  offering: Offering;
}

const markdownComponents: Components = {
  p: Typography,
  strong: ({ children, ...rest }) => (
    <Typography component="span" fontWeight="semibold" {...rest}>
      {children}
    </Typography>
  ),
  a: ({ children, ...rest }) => (
    <Link
      sx={{ '&:visited': { color: ({ palette }) => palette.aqua[500] } }}
      fontWeight="bold"
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
    >
      {children}
    </Link>
  ),
};

export const PropertyDetailsDescription = ({
  offering: { assetType, description, id, properties },
  ...rest
}: PropertyDetailsDescriptionProps) => {
  const strOfferingData = useStrOfferingData(id);

  const { address } = properties[0] || {};

  return (
    <Stack gap={4} px={3} my={{ xs: 2, md: 4 }} {...rest}>
      <Box display="flex" flexDirection="column" gap={2}>
        <ReactMarkdown components={markdownComponents}>{description}</ReactMarkdown>
      </Box>
      {assetType === AssetType.STR && (
        <div>
          <Typography variant="body1.bold">
            <FormattedMessage id="property-description-text" defaultMessage="Amenities" />
          </Typography>
          <ul>
            {strOfferingData.amenities.map((amenity: string, idx: number) => (
              <li key={`property-description-str-amenity-${idx}`}>
                <Typography variant="body1">
                  <FormattedMessage id={`property-description-str-amenity-${idx}`} defaultMessage={amenity} />
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* We only want to include the address if the address isn't already in the description. */}
      {!description.includes('Address') && address && (
        <Typography fontWeight="semibold">
          <FormattedMessage
            id="address-string"
            defaultMessage="Address: {address}"
            values={{ address: LocationUtils.getAddressString(address) }}
          />
        </Typography>
      )}
    </Stack>
  );
};
